import React from "react";

/**
 * Sibling URLs for a stored product image path, e.g. "/api/static/products/x.png?v=5" ->
 * { avif: ".../x.avif?v=5", webp: ".../x.webp?v=5", original: url }.
 * The backend serves the original bytes if a sibling is missing, so these URLs are always safe.
 */
export const imageSources = (url) => {
  if (!url || typeof url !== "string") return null;
  const m = url.match(/^(.*)\.(png|jpe?g)(\?.*)?$/i);
  if (!m || !url.startsWith("/api/static/")) return null;
  const [, base, , query = ""] = m;
  const set = (fmt) => `${base}-800.${fmt}${query} 800w, ${base}.${fmt}${query} 1600w`;
  return { avif: `${base}.avif${query}`, webp: `${base}.webp${query}`, avifSet: set("avif"), webpSet: set("webp"), original: url };
};

/**
 * <picture> that lets the browser choose AVIF, then WebP, then the original PNG - cutting product images
 * from ~570 KB to ~16-26 KB with no visible change. Pass `sizes` for card-sized renders to also offer the 800px
 * variant (≈6 KB). Props are forwarded to the <img>.
 */
export const ProductImage = React.forwardRef(({ src, alt = "", loading = "lazy", decoding = "async", sizes, ...rest }, ref) => {
  const s = imageSources(src);
  if (!s) return <img ref={ref} src={src} alt={alt} loading={loading} decoding={decoding} {...rest} />;
  return (
    <picture>
      <source srcSet={sizes ? s.avifSet : s.avif} sizes={sizes} type="image/avif" />
      <source srcSet={sizes ? s.webpSet : s.webp} sizes={sizes} type="image/webp" />
      <img ref={ref} src={s.original} alt={alt} loading={loading} decoding={decoding} {...rest} />
    </picture>
  );
});
ProductImage.displayName = "ProductImage";
